import React, { useMemo, useState } from "react";
import { Alert, Card, Menu, Select, Table, Tag, message } from "antd";
import { CheckOutlined, CopyOutlined, MailOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import { formatPrice } from "../../../functions/formatters";
import { useSupplementInvoices } from "../../../data/hooks";

const TYPE_LABELS = {
  additional_payment: "Bijbetalen",
  refund_due: "Terugbetalen",
};

const STATUS_TAGS = {
  open: { color: "orange", label: "Open" },
  paid: { color: "green", label: "Betaald" },
  manual: { color: "blue", label: "Handmatig afgehandeld" },
  cancelled: { color: "default", label: "Geannuleerd" },
  paid_after_cancel: { color: "red", label: "Controleer handmatig" },
  resolved: { color: "green", label: "Afgehandeld" },
};

const SupplementInvoices = () => {
  const [statusFilter, setStatusFilter] = useState(["open"]);
  const [typeFilter, setTypeFilter] = useState(null);
  const [shopFilter, setShopFilter] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const {
    supplements,
    fetchFailures,
    error,
    isLoading,
    markSupplementPaidManually,
    markRefundResolved,
    resendSupplementPaymentMail,
  } = useSupplementInvoices();

  const filteredSupplements = useMemo(
    () =>
      supplements.filter(
        (row) =>
          (statusFilter.length === 0 || statusFilter.includes(row.status)) &&
          (!typeFilter || row.type === typeFilter) &&
          (!shopFilter || row.shopSource === shopFilter),
      ),
    [supplements, statusFilter, typeFilter, shopFilter],
  );

  const handleCopyLink = (paymentUrl) => {
    navigator.clipboard.writeText(paymentUrl);
    message.success("Betaallink gekopieerd");
  };

  const handleMarkDone = async (supplement) => {
    try {
      if (supplement.type === "refund_due") {
        await markRefundResolved(supplement);
      } else {
        await markSupplementPaidManually(supplement);
      }
      message.success("Bijgewerkt");
    } catch (err) {
      console.error("Error updating supplement:", err);
      message.error("Er ging iets mis bij het bijwerken");
    }
  };

  const handleResend = async (supplement) => {
    setResendingId(supplement.id);
    try {
      await resendSupplementPaymentMail(supplement);
      message.success("Betaallink opnieuw verstuurd");
    } catch (err) {
      console.error("Error resending supplement payment mail:", err);
      message.error(err.message || "Versturen mislukt - probeer het nog eens");
    } finally {
      setResendingId(null);
    }
  };

  const columns = [
    {
      title: "Aangemaakt op",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
      defaultSortOrder: "descend",
      render: (createdAt) => (createdAt ? dayjs(createdAt).format("DD-MM-YYYY HH:mm") : "-"),
    },
    { title: "Order", dataIndex: "orderReference", key: "orderReference" },
    {
      title: "Shop",
      dataIndex: "shopSource",
      key: "shopSource",
      render: (shopSource) => <Tag>{shopSource}</Tag>,
    },
    { title: "Klant", key: "customer", render: (_, row) => `${row.customerName || ""} (${row.customerEmail || ""})` },
    { title: "Type", dataIndex: "type", key: "type", render: (type) => TYPE_LABELS[type] || type },
    {
      title: "Bedrag",
      dataIndex: "amountCents",
      key: "amountCents",
      render: (amountCents) => <span style={{ whiteSpace: "nowrap" }}>€ {formatPrice((amountCents || 0) / 100)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const tag = STATUS_TAGS[status] || { color: "default", label: status };
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: "Link verzonden op",
      dataIndex: "paymentLinkSentAt",
      key: "paymentLinkSentAt",
      sorter: (a, b) => (a.paymentLinkSentAt || 0) - (b.paymentLinkSentAt || 0),
      render: (paymentLinkSentAt, row) =>
        paymentLinkSentAt ? (
          dayjs(paymentLinkSentAt).format("DD-MM-YYYY HH:mm")
        ) : row.type === "additional_payment" && row.shopSource === "VCE" ? (
          <Tag color="red">Nooit verzonden</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Acties",
      key: "actions",
      render: (_, row) => <EllipsisDropdown menu={actionsMenu(row)} />,
    },
  ];

  const actionsMenu = (row) => (
    <Menu>
      {row.paymentUrl && row.status === "open" && (
        <Menu.Item key="copy">
          <a onClick={() => handleCopyLink(row.paymentUrl)}>
            <Flex alignItems="center">
              <CopyOutlined />
              <span style={{ marginLeft: 4 }}>Kopieer link</span>
            </Flex>
          </a>
        </Menu.Item>
      )}
      {row.paymentUrl && (row.status === "open" || row.status === "paid_after_cancel") && (
        <Menu.Item key="resend" disabled={resendingId === row.id}>
          <a onClick={() => handleResend(row)}>
            <Flex alignItems="center">
              <MailOutlined />
              <span style={{ marginLeft: 4 }}>Opnieuw versturen</span>
            </Flex>
          </a>
        </Menu.Item>
      )}
      {(row.status === "open" || row.status === "paid_after_cancel") && (
        <Menu.Item key="mark-done">
          <a onClick={() => handleMarkDone(row)}>
            <Flex alignItems="center">
              <CheckOutlined />
              <span style={{ marginLeft: 4 }}>
                {row.type === "refund_due" ? "Markeer afgehandeld" : "Markeer handmatig betaald"}
              </span>
            </Flex>
          </a>
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Card title="Nafacturaties">
      <Flex mobileFlex={false} justifyContent="start" style={{ gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Select
          mode="multiple"
          allowClear
          placeholder="Status"
          style={{ minWidth: 220 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={Object.entries(STATUS_TAGS).map(([value, { label }]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="Type"
          style={{ minWidth: 160 }}
          value={typeFilter}
          onChange={setTypeFilter}
          options={Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="Shop"
          style={{ minWidth: 120 }}
          value={shopFilter}
          onChange={setShopFilter}
          options={[
            { value: "VCE", label: "VCE" },
            { value: "HVO", label: "HVO" },
          ]}
        />
      </Flex>
      {(error || fetchFailures.length > 0) && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Kon niet alle nafacturaties ophalen"
          description={
            <>
              <p style={{ marginBottom: 4 }}>{error ? error.message : fetchFailures.join(" | ")}</p>
              <p style={{ margin: 0 }}>
                Meestal betekent dit dat de Firestore rules deze CMS-gebruiker geen leestoegang geven tot de
                <code> supplements</code>-subcollectie. Zie de browser console voor het volledige foutbericht.
              </p>
            </>
          }
        />
      )}
      <Table
        columns={columns}
        dataSource={filteredSupplements}
        rowKey={(row) => `${row.orderId}-${row.id}`}
        loading={isLoading}
      />
    </Card>
  );
};

export default SupplementInvoices;
